import { BroadviewClientError } from "@broadview/api-client";
import type { TenantContext } from "@broadview/api-client";
import { queryOptions, type QueryClient } from "@tanstack/react-query";
import type { PortalRuntime } from "./portal-runtime";

export type ClaimReader = NonNullable<PortalRuntime["claims"]>;
export type ClaimFilters = Omit<Parameters<ClaimReader["search"]>[0], "tenantId">;

export function claimWorkspaceStateKey(cache: QueryClient, tenantId: string) {
  const key = ["portal", "claim-evidence", tenantId, "workspace-state"] as const;
  // Unobserved UI state must survive idle shell remounts. Retain only this exact
  // in-memory key; evidence data queries keep their ordinary GC policy.
  // QueryClient.clear() at sign-out still removes retained state.
  cache.setQueryDefaults(key, { gcTime: Infinity });
  return key;
}

export function canReadClaimEvidence(tenant: TenantContext) {
  return (
    tenant.permissions.includes("evidence.read") &&
    (tenant.evidenceScopes ?? []).length > 0 &&
    (tenant.evidenceScopes ?? []).every(
      (scope) =>
        scope.scopeType === "ORGANIZATIONAL_SCOPE" ||
        (scope.scopeType === "TENANT" && scope.scopeId === tenant.tenantId),
    )
  );
}

const retry = (attempt: number, error: unknown) =>
  error instanceof BroadviewClientError && !error.retryable ? false : attempt < 1;

export function claimSearchOptions(
  reader: ClaimReader | undefined,
  tenant: TenantContext,
  filters: ClaimFilters,
) {
  return queryOptions({
    queryKey: ["portal", "claim-evidence", tenant.tenantId, "search", filters] as const,
    enabled: Boolean(reader) && canReadClaimEvidence(tenant),
    queryFn: () => {
      if (!reader || !canReadClaimEvidence(tenant))
        throw new Error("Claim evidence access unavailable");
      return reader.search({ tenantId: tenant.tenantId, ...filters });
    },
    staleTime: 0,
    retry,
  });
}

export function claimDetailOptions(
  reader: ClaimReader | undefined,
  tenant: TenantContext,
  id: string | null,
) {
  return queryOptions({
    queryKey: ["portal", "claim-evidence", tenant.tenantId, "detail", id] as const,
    enabled: Boolean(reader && id) && canReadClaimEvidence(tenant),
    queryFn: () => {
      if (!reader || !id || !canReadClaimEvidence(tenant))
        throw new Error("Claim evidence access unavailable");
      return reader.get({ tenantId: tenant.tenantId, id });
    },
    staleTime: 0,
    retry,
  });
}

export function claimEvidenceError(error: unknown) {
  if (error instanceof BroadviewClientError) {
    if (error.code === "FORBIDDEN")
      return "Claim evidence permission or explicit scope is unavailable for this Division.";
    if (error.code === "UNAUTHENTICATED")
      return "Your verified session is required to read claim evidence.";
    if (error.code === "NOT_FOUND")
      return "Claim evidence was not found or is unavailable within your scope.";
    if (error.code === "CONTRACT_MISMATCH")
      return "Claim evidence could not be safely verified. No substitute data is shown.";
  }
  return "Claim evidence is unavailable. No substitute data is shown.";
}
